import type { Health } from '@ops/contracts'

import { cx } from '../lib/cx'
import { SEVERIDAD_TRAZO } from './tokens'

/**
 * Serie chica al lado de un número. SVG a mano y no Recharts: el sparkline mide
 * 64×26px, no necesita ejes, tooltip ni `ResponsiveContainer`, y sin
 * `ResizeObserver` renderiza igual bajo jsdom. Recharts se reserva para los
 * gráficos con ejes de las pantallas.
 *
 * Un `null` en la serie es un hueco, **no un cero**: un mes sin cargar dibujado
 * en cero se lee como un mes en el que la empresa no facturó. Los tramos se
 * cortan en el hueco.
 */
export interface SparklineProps {
  /** Serie del más viejo al más nuevo. `null` = sin dato ese período. */
  valores: readonly (number | null)[]
  health?: Health
  width?: number
  height?: number
  /** Marca el último punto con dato. */
  marcarUltimo?: boolean
  /**
   * Nombre accesible. Sin esto el gráfico es decorativo: el valor y el delta ya
   * están en texto al lado, así que repetirlos es ruido para un lector.
   */
  label?: string
  className?: string
}

interface Punto {
  x: number
  y: number
}

/** Convierte la serie a coordenadas. Exportada para poder testear la escala. */
export function puntosDe(
  valores: readonly (number | null)[],
  width: number,
  height: number,
  padding = 2,
): (Punto | null)[] {
  const conDato = valores.filter((v): v is number => v !== null)
  if (conDato.length === 0) return []

  const min = Math.min(...conDato)
  const max = Math.max(...conDato)
  const rango = max - min
  const alto = height - padding * 2
  const paso = valores.length > 1 ? width / (valores.length - 1) : 0

  return valores.map((valor, i) => {
    if (valor === null) return null
    // Serie plana: se dibuja en el medio, no pegada al borde de arriba.
    const t = rango === 0 ? 0.5 : (valor - min) / rango
    return { x: i * paso, y: padding + alto * (1 - t) }
  })
}

function tramos(puntos: (Punto | null)[]): Punto[][] {
  const salida: Punto[][] = []
  let actual: Punto[] = []
  for (const punto of puntos) {
    if (punto === null) {
      if (actual.length > 0) salida.push(actual)
      actual = []
      continue
    }
    actual.push(punto)
  }
  if (actual.length > 0) salida.push(actual)
  return salida
}

export function Sparkline({
  valores,
  health = 'unknown',
  width = 64,
  height = 26,
  marcarUltimo = true,
  label,
  className,
}: SparklineProps) {
  const puntos = puntosDe(valores, width, height)
  if (puntos.length === 0) return null

  const trazo = SEVERIDAD_TRAZO[health]
  const segmentos = tramos(puntos)
  const ultimo = [...puntos].reverse().find((p): p is Punto => p !== null)
  const decorativo = label === undefined

  return (
    <svg
      data-testid="sparkline"
      data-health={health}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      fill="none"
      focusable="false"
      aria-hidden={decorativo ? true : undefined}
      role={decorativo ? undefined : 'img'}
      aria-label={label}
      className={cx('block shrink-0 overflow-visible', className)}
    >
      {segmentos.map((segmento, i) =>
        segmento.length === 1 ? (
          <circle
            key={i}
            cx={segmento[0]?.x}
            cy={segmento[0]?.y}
            r={1.5}
            fill={trazo}
          />
        ) : (
          <polyline
            key={i}
            points={segmento.map((p) => `${p.x},${p.y}`).join(' ')}
            stroke={trazo}
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ),
      )}
      {marcarUltimo && ultimo !== undefined ? (
        <circle
          data-testid="sparkline-ultimo"
          cx={ultimo.x}
          cy={ultimo.y}
          r={2}
          fill={trazo}
        />
      ) : null}
    </svg>
  )
}
